import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Button,
  Card,
  CardHeader,
  CardSection,
  EducationUtils,
  Gutter,
  IconButton,
  Layout,
  LayoutItem,
  Paragraph,
  ParagraphHeader,
  SmallHeader,
  useApi,
} from '@axiom/ui';
import type {
  Candidate,
  CandidateProfileIssue,
  Education as EducationType,
} from '@axiom/validation';

import { AreasOfStudyApi } from '../../api/areas-of-study-api';
import { EducationUtil } from '../../utils/education-util';

import { EducationBannerPrompt } from './EducationBannerPrompt';
import { EducationDeleteModal } from './EducationDeleteModal';
import { EducationEditModal } from './EducationEditModal';

export type EducationProps = {
  candidate: Candidate;
  issues: CandidateProfileIssue[];
};

export const Education = ({ candidate, issues }: EducationProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [educationDeleteId, setEducationDeleteId] = useState<
    EducationType['id'] | null
  >(null);
  const [educationEdit, setEducationEdit] = useState<
    EducationType | null | Record<PropertyKey, never>
  >(null);
  const [{ data: areasOfStudy }] = useApi(AreasOfStudyApi.readAreasOfStudy());
  const education = useMemo(() => {
    return EducationUtils.sortDisplay(candidate.degrees ?? []);
  }, [candidate.degrees]);

  const hasIssue = (id: EducationType['id']) => {
    return issues.some(({ entityId }) => entityId === id);
  };

  useEffect(() => {
    if (searchParams.get('modal') === 'education') {
      const eduId = searchParams.get('id');
      const edu = education.find(e => e.id === eduId);

      if (edu) {
        setEducationEdit(edu);
      } else {
        setEducationEdit({});
      }

      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  return (
    <Card name="EDUCATION">
      <CardHeader>
        <Layout position="middle">
          <LayoutItem fluid>
            <SmallHeader>Education</SmallHeader>
          </LayoutItem>
          <LayoutItem>
            <Button
              name="EDUCATION_ADD_BUTTON"
              pattern="secondary"
              variation="minimal"
              icon="plus"
              onClick={() => {
                setEducationEdit({});
              }}
            >
              Add
            </Button>
          </LayoutItem>
        </Layout>
      </CardHeader>
      {education.length === 0 && (
        <CardSection>
          <EducationBannerPrompt
            onOpenEdit={() => {
              setEducationEdit({});
            }}
          />
          <Gutter bottom="16px" />
        </CardSection>
      )}
      {education.map(edu => {
        return (
          <CardSection name="EDUCATION_ITEM" key={edu.id} divider>
            <Layout position="middle">
              <LayoutItem fluid>
                <ParagraphHeader name="INSTITUTION">
                  {edu.institution}
                </ParagraphHeader>
              </LayoutItem>
              <LayoutItem>
                <Layout horizontalGutter="8px" position="middle">
                  {!edu.isPrimary && (
                    <IconButton
                      icon="trash"
                      pattern="secondary"
                      variation="minimal"
                      name="DELETE_EDUCATION_BUTTON"
                      onClick={() => {
                        setEducationDeleteId(edu.id);
                      }}
                    />
                  )}
                  <IconButton
                    icon="pencil"
                    pattern="secondary"
                    variation="minimal"
                    name="EDIT_EDUCATION_BUTTON"
                    onClick={() => {
                      setEducationEdit(edu);
                    }}
                  />
                </Layout>
              </LayoutItem>
            </Layout>
            <Paragraph name="EDUCATION_DETAIL">
              {EducationUtil.formatEducationDetails(edu)}
            </Paragraph>
            {hasIssue(edu.id) && (
              <>
                <Gutter bottom="16px" />
                <EducationBannerPrompt
                  onOpenEdit={() => {
                    setEducationEdit(edu);
                  }}
                  name={`EDUCATION_MISSING_DETAILS_BANNER_${edu.id}`}
                />
              </>
            )}
          </CardSection>
        );
      })}
      {!!educationDeleteId && (
        <EducationDeleteModal
          candidateId={candidate.id}
          educationId={educationDeleteId}
          onClose={() => {
            setEducationDeleteId(null);
          }}
        />
      )}
      {!!educationEdit && (
        <EducationEditModal
          educations={education}
          modifyEducation={educationEdit}
          candidateId={candidate.id}
          areasOfStudy={areasOfStudy}
          onClose={() => {
            setEducationEdit(null);
          }}
        />
      )}
    </Card>
  );
};
